import { useEffect, useState } from 'react';
import { Alert, Form, Select, Space, Table, Tag, Typography, message } from 'antd';
import Modal from 'antd/es/modal/Modal';
import { SwapOutlined } from '@ant-design/icons';
import { Authentication } from '@leedpulse-monorepo/classes';
import migrateSubscriptionSender, {
  SenderMigrationOtherSender,
  SenderMigrationResponse,
} from '../../../../domain/use_cases/subscriptions/migrateSubscriptionSender';

interface ReassignSenderModalProps {
  open: boolean;
  onClose: () => void;
  subscriptionId: string;
  /** Numbers the subscription currently holds — the only valid destinations. */
  senders: string[];
  auth: Authentication | null;
  onSuccess: () => void;
}

/**
 * Maintenance tool: moves every document of this subscription carrying one
 * number onto another one of its numbers. Cleans up after a swap done by hand,
 * where `sender[]` was edited without moving the documents — the contacts then
 * keep a number the subscription no longer holds, outbound SMS keep going out
 * from it, and nothing can reach them again.
 *
 * Replacing a number is NOT this screen: that is the swap button on the
 * subscription page.
 */
const ReassignSenderModal: React.FC<ReassignSenderModalProps> = ({
  open,
  onClose,
  subscriptionId,
  senders,
  auth,
  onSuccess,
}) => {
  const [destination, setDestination] = useState<string>(senders[0] ?? '');
  const [source, setSource] = useState<string | undefined>(undefined);
  const [response, setResponse] = useState<SenderMigrationResponse | null>(null);
  const [inventory, setInventory] = useState<SenderMigrationOtherSender[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  const plan = response?.plan ?? null;
  const isApplied = response?.applied === true;
  // Lancé mais pas attendu : la réécriture tourne côté serveur, il n'y a pas de
  // résultat à afficher. Le formulaire se verrouille comme s'il était terminé.
  const isDetached = response?.detached === true;
  const isDone = isApplied || isDetached;
  const hasBlockers = (plan?.blockers.length ?? 0) > 0;

  const call = async (apply: boolean, sSource?: string, sDestination = destination) => {
    if (!auth || !sDestination) return;
    const setLoading = apply ? setIsBusy : setIsLoading;
    setLoading(true);
    try {
      const result = await migrateSubscriptionSender(
        {
          subscription_id: subscriptionId,
          // No old_sender: nothing is replaced, documents are only moved over.
          new_sender: sDestination,
          additional_senders: sSource ? [sSource] : [],
          apply,
        },
        auth
      );
      setResponse(result);
      setInventory(result.plan.other_senders.filter((o) => o.sender !== sDestination));
      if (result.applied) {
        message.success(`${result.result?.migrated_documents ?? 0} document(s) déplacé(s) sur ${sDestination}.`);
        onSuccess();
      }
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Erreur lors de l'analyse.");
    } finally {
      setLoading(false);
    }
  };

  // Inventory of the numbers carrying documents, as soon as the modal opens.
  useEffect(() => {
    if (open && destination) void call(false, undefined, destination);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const strays = inventory.filter((o) => !o.still_held);

  const handleSource = (sSender: string) => {
    setSource(sSender);
    void call(false, sSender);
  };

  const handleDestination = (sSender: string) => {
    setDestination(sSender);
    setSource(undefined);
    void call(false, undefined, sSender);
  };

  const handleClose = () => {
    setSource(undefined);
    setResponse(null);
    setInventory([]);
    onClose();
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <SwapOutlined size={20} style={{ marginRight: 6 }} />
          <span style={{ fontSize: '16px', fontWeight: 600 }}>Réaffecter un numéro</span>
        </div>
      }
      centered
      open={open}
      onCancel={handleClose}
      closable={!isBusy}
      maskClosable={!isBusy}
      okText={
        isApplied
          ? 'Déplacement terminé'
          : isDetached
            ? 'Déplacement lancé'
            : plan && source
              ? `Déplacer ${plan.total_documents} document(s)`
              : 'Déplacer'
      }
      cancelText={isDone ? 'Fermer' : 'Annuler'}
      confirmLoading={isBusy}
      okButtonProps={{ danger: true, disabled: !source || !plan || hasBlockers || isDone || isLoading }}
      onOk={() => call(true, source)}
      width={720}
    >
      {senders.length === 0 ? (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 16 }}
          message="Aucun numéro sur cet abonnement"
          description="Il faut au moins un numéro rattaché pour pouvoir y regrouper des documents."
        />
      ) : (
        <Form layout="vertical" style={{ marginTop: '1.5rem' }}>
          {isDetached && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={`Déplacement de ${plan?.total_documents ?? 0} document(s) lancé en arrière-plan`}
              description={
                <>
                  C'est trop volumineux pour attendre la réponse à l'écran. La réécriture
                  continue côté serveur, tu peux fermer cette fenêtre. Reviens dans quelques
                  minutes et relance l'analyse : quand {source} ne remonte plus dans la liste,
                  tout est passé.
                </>
              }
            />
          )}

          {isApplied && (
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
              message={`${response?.result?.migrated_documents ?? 0} document(s) déplacé(s) sur ${destination}`}
              description={
                <>
                  Plus aucun document de cet abonnement ne porte {source}. Trace d'audit :{' '}
                  <Typography.Text code>Logs/{response?.result?.log_id}</Typography.Text>
                </>
              }
            />
          )}

          {!isDone && strays.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={`${strays.length} numéro(s) égaré(s)`}
              description={
                <>
                  Ces numéros ne sont plus rattachés à l'abonnement, mais portent encore ses
                  documents. Personne ne peut plus les atteindre, et les SMS sortants partent
                  toujours depuis eux.
                  {strays.map((o) => (
                    <div key={o.sender} style={{ marginTop: 4 }}>
                      <Tag color="orange">{o.sender}</Tag> {o.contacts} contact(s)
                    </div>
                  ))}
                </>
              }
            />
          )}

          <Form.Item label="Numéro à vider">
            <Select
              value={source}
              placeholder="Sélectionner le numéro dont les documents doivent partir"
              loading={isLoading}
              disabled={isDone}
              onChange={handleSource}
              options={inventory.map((o) => ({
                value: o.sender,
                label: (
                  <Space>
                    {o.sender}
                    <Typography.Text type="secondary">{o.contacts} contact(s)</Typography.Text>
                    {o.still_held ? (
                      <Tag color="blue">ligne active</Tag>
                    ) : (
                      <Tag color="orange">égaré</Tag>
                    )}
                  </Space>
                ),
              }))}
            />
          </Form.Item>

          <Form.Item
            label="Numéro de destination"
            extra="Uniquement les numéros rattachés à cet abonnement."
          >
            <Select
              value={destination}
              disabled={isDone}
              onChange={handleDestination}
              options={senders.map((s) => ({ value: s, label: s }))}
            />
          </Form.Item>

          {source && inventory.find((o) => o.sender === source)?.still_held && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Ce numéro est une ligne active"
              description={`${source} est toujours rattaché à l'abonnement. Déplacer ses documents ne le supprime pas, mais plus aucun contact ne sera rattaché à cette ligne.`}
            />
          )}

          {plan?.blockers.map((blocker) => (
            <Alert
              key={blocker.code + blocker.message}
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message="Opération bloquée"
              description={blocker.message}
            />
          ))}

          {source && plan?.exceeds_sync_limit && !isDone && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Déplacement trop volumineux pour être attendu à l'écran"
              description={`${plan.total_documents} documents : le déplacement sera lancé en arrière-plan et tu devras revenir relancer l'analyse pour vérifier qu'il ne reste rien.`}
            />
          )}

          {source && plan && !hasBlockers && (
            <Table
              size="small"
              pagination={false}
              title={() =>
                isApplied
                  ? 'Documents déplacés'
                  : isDetached
                    ? 'Documents en cours de déplacement'
                    : 'Documents qui vont être déplacés'
              }
              dataSource={plan.counts.map((c) => ({ key: c.collection, ...c }))}
              columns={[
                { title: 'Collection', dataIndex: 'collection', key: 'collection' },
                { title: 'Documents', dataIndex: 'total', key: 'total' },
              ]}
            />
          )}

          <Typography.Text type="secondary" style={{ fontSize: '13px', display: 'block', marginTop: 16 }}>
            Déplace les contacts, SMS, appels et messageries de cet abonnement d'un numéro vers un
            autre. Aucun numéro n'est remplacé et aucune redirection n'est touchée — pour remplacer un
            numéro, utiliser le bouton de swap sur la fiche de l'abonnement.
          </Typography.Text>
        </Form>
      )}
    </Modal>
  );
};

export default ReassignSenderModal;
